// Migration utilities for loyalty settings

export interface LoyaltySettings {
  enabled: boolean;
  points: {
    per_visit: number;
    per_referral: number;
    per_amount: number;
    expiration_days: number;
  };
  diamonds: {
    per_visits: number;
    visits_required: number;
    per_amount: number;
  };
  levels: {
    [key: string]: {
      diamonds_required: number;
      benefits: {
        services_discount: number;
        products_discount: number;
        free_appointment_every: number | null;
        birthday_appointment: boolean;
      };
    };
  };
}

export const DEFAULT_LOYALTY_SETTINGS: LoyaltySettings = {
  enabled: true,
  points: {
    per_visit: 10,
    per_referral: 50,
    per_amount: 5,
    expiration_days: 365
  },
  diamonds: {
    per_visits: 1,
    visits_required: 10,
    per_amount: 1
  },
  levels: {
    silver: {
      diamonds_required: 0,
      benefits: {
        services_discount: 5,
        products_discount: 5,
        free_appointment_every: null,
        birthday_appointment: false
      }
    },
    gold: {
      diamonds_required: 5,
      benefits: {
        services_discount: 10,
        products_discount: 10,
        free_appointment_every: 10,
        birthday_appointment: true
      }
    },
    diamond: {
      diamonds_required: 10,
      benefits: {
        services_discount: 15,
        products_discount: 15,
        free_appointment_every: 8,
        birthday_appointment: true
      }
    },
    vip: {
      diamonds_required: 20,
      benefits: {
        services_discount: 20,
        products_discount: 20,
        free_appointment_every: 5,
        birthday_appointment: true
      }
    }
  }
};

// Migrate old format to new format
export function migrateLoyaltySettings(oldSettings: any): LoyaltySettings {
  if (!oldSettings) return DEFAULT_LOYALTY_SETTINGS;

  try {
    const migrated: LoyaltySettings = {
      enabled: oldSettings.enabled ?? DEFAULT_LOYALTY_SETTINGS.enabled,
      points: {
        per_visit: oldSettings.points?.per_visit ?? DEFAULT_LOYALTY_SETTINGS.points.per_visit,
        per_referral: oldSettings.points?.per_referral ?? DEFAULT_LOYALTY_SETTINGS.points.per_referral,
        per_amount: oldSettings.points?.per_amount ?? DEFAULT_LOYALTY_SETTINGS.points.per_amount,
        expiration_days: oldSettings.points?.expiration_days ?? DEFAULT_LOYALTY_SETTINGS.points.expiration_days
      },
      diamonds: {
        // Support old field names
        per_visits:
          oldSettings.diamonds?.per_visits ??
          oldSettings.diamonds?.per_consecutive_visits ??
          DEFAULT_LOYALTY_SETTINGS.diamonds.per_visits,
        visits_required:
          oldSettings.diamonds?.visits_required ??
          oldSettings.diamonds?.consecutive_visits_required ??
          DEFAULT_LOYALTY_SETTINGS.diamonds.visits_required,
        per_amount: oldSettings.diamonds?.per_amount ?? DEFAULT_LOYALTY_SETTINGS.diamonds.per_amount
      },
      levels: {}
    };

    // Migrate levels
    for (const [levelKey, defaultLevel] of Object.entries(DEFAULT_LOYALTY_SETTINGS.levels)) {
      const oldLevel = oldSettings.levels?.[levelKey];
      migrated.levels[levelKey] = {
        diamonds_required: oldLevel?.diamonds_required ?? defaultLevel.diamonds_required,
        benefits: {
          services_discount: oldLevel?.benefits?.services_discount ?? defaultLevel.benefits.services_discount,
          products_discount: oldLevel?.benefits?.products_discount ?? defaultLevel.benefits.products_discount,
          free_appointment_every: oldLevel?.benefits?.free_appointment_every ?? defaultLevel.benefits.free_appointment_every,
          birthday_appointment: oldLevel?.benefits?.birthday_appointment ?? defaultLevel.benefits.birthday_appointment
        }
      };
    }

    return migrated;
  } catch (error) {

    return DEFAULT_LOYALTY_SETTINGS;
  }
}

// Validate loyalty settings
export function validateLoyaltySettings(settings: LoyaltySettings): string | null {
  if (!settings) return 'הגדרות לא תקינות';

  // Validate points
  if (settings.points.per_visit < 0) return 'נקודות לביקור חייבות להיות חיוביות';
  if (settings.points.per_amount < 0) return 'נקודות לסכום חייבות להיות חיוביות';
  if (settings.points.per_referral < 0) return 'נקודות להפניה חייבות להיות חיוביות';
  if (settings.points.expiration_days < 0) return 'תוקף נקודות חייב להיות חיובי';

  // Validate diamonds
  if (settings.diamonds.per_visits < 0) return 'יהלומים לביקורים חייבים להיות חיוביים';
  if (settings.diamonds.visits_required < 1) return 'מספר ביקורים נדרש חייב להיות לפחות 1';
  if (settings.diamonds.per_amount < 0) return 'יהלומים לסכום חייבים להיות חיוביים';

  // Validate levels progression
  const levelKeys = ['silver', 'gold', 'diamond', 'vip'];
  for (let i = 0; i < levelKeys.length - 1; i++) {
    const currentKey = levelKeys[i];
    const nextKey = levelKeys[i + 1];

    const current = settings.levels[currentKey];
    const next = settings.levels[nextKey];

    if (!current || !next) return `דרגה ${currentKey} או ${nextKey} חסרה`;

    if (current.diamonds_required >= next.diamonds_required) {
      return `כמות היהלומים לדרגת ${currentKey} חייבת להיות פחות מ-${nextKey}`;
    }

    // Validate benefits
    if (current.benefits.services_discount < 0 || current.benefits.services_discount > 100) {
      return 'הנחה על שירותים חייבת להיות בין 0 ל-100';
    }
    if (current.benefits.products_discount < 0 || current.benefits.products_discount > 100) {
      return 'הנחה על מוצרים חייבת להיות בין 0 ל-100';
    }
  }

  return null;
}

// Ensure settings exist or create defaults
export async function ensureLoyaltySettings(supabase: any, businessId: string): Promise<LoyaltySettings> {
  const { data, error } = await supabase
    .from('businesses')
    .select('settings')
    .eq('id', businessId)
    .single();

  if (error) throw error;

  // If loyalty settings don't exist, create them
  if (!data?.settings?.loyalty) {
    const newSettings = {
      ...data?.settings,
      loyalty: DEFAULT_LOYALTY_SETTINGS
    };

    const { error: updateError } = await supabase
      .from('businesses')
      .update({ settings: newSettings })
      .eq('id', businessId);

    if (updateError) throw updateError;

    return DEFAULT_LOYALTY_SETTINGS;
  }

  // Migrate existing settings
  return migrateLoyaltySettings(data.settings.loyalty);
}
